// THE ONE RULE about offering an expense category.
//
// A category may not be offered — or pre-filled — until someone has answered
// "is this 529-eligible?", because the answer decides WHICH LIST the category
// comes from, and the two lists are unrelated things:
//
//   Yes — 529-eligible : the IRS list for the beneficiary's education group.
//                        Fixed by tax rules, read-only, not the family's to edit.
//   No  — not eligible : the family's own list, kept per beneficiary and edited
//                        by the account owner in Settings.
//
// Showing either list before the question is answered offers categories from
// the wrong list, and a pre-filled one quietly answers the question on the
// owner's behalf. On the receipt-import path that classification is the first
// step toward a tax record, so it must be the owner's, not a guess.
//
// This lives in one file because a list of screens has no completion condition.
// The classify dialog established the rule (ZEN-114) and the split panel
// followed it; ZEN-282 found the imports and receipt-review screens doing the
// opposite, so the rule moved here and the screens ask.
//
// PARTLY USING THIS MODULE: components/ExpenseDialog.jsx now takes its ELIGIBLE
// LIST from irsCategoriesFor below (ZEN-100 removed the three hand-rolled
// copies it had, which each filtered a catch-all out of GROUP_CATEGORIES by
// hand and each fell back to a retired flat list that contained another one).
// What it still carries inline is the SURROUNDING RULE — the eligible/non-529
// branch and the reconcile-on-change behaviour that categoryChoicesFor and
// reconcileCategory encode below. It behaves identically; this module was
// written from it. Folding that half in is still ZEN-287.
import { GROUP_CATEGORIES, DEFAULT_EXPENSE_GROUP } from "@/lib/format";

// The sentinel the NON-529 dropdowns use for "let me type a one-off name".
// It is an affordance offered by the screen, never a category in either list.
//
// IT BELONGS TO THE NON-529 SIDE ONLY, and that is a ruling, not a convention
// (owner, 2026-08-15). The family's own per-beneficiary list keeps a catch-all
// — a home for the concert ticket not worth naming. The 529-ELIGIBLE list never
// carries one, because there is no "Other" allowable under Section 529 and that
// list is the IRS read-only reference, not the family's to extend. It is still
// filtered out of the family list below because it names an ACTION, not a
// category: a row must never be stored carrying this literal string.
export const OTHER_MANUAL = "Other — enter manually";

// What the screen shows in the category's place while the question is open.
export const CATEGORY_LOCKED_HINT = "Answer the 529 question first";

// Three-valued on purpose: null/undefined means NOBODY HAS ANSWERED, which is
// a real and reachable state (an imported row saved as a draft), not a false.
export const isEligibilityAnswered = (eligible) =>
  eligible === true || eligible === false;

// The IRS list for an education group. NO FILTER — the source is clean. (ZEN-100)
//
// This used to read `(GROUP_CATEGORIES[g] || CATEGORIES).filter(...)`, and both
// halves of that were wrong. The filter was a workaround for a catch-all that
// should never have been in GROUP_CATEGORIES; it has been removed at the
// source, so filtering here would only hide a regression instead of failing on
// it. And the `|| CATEGORIES` fallback reached for a retired flat list whose
// own "Other" the filter did not even match — so an unresolved education group
// offered an impossible category on the eligible side. An unknown group now
// falls back to a REAL group's real list, the same way the server does.
export const irsCategoriesFor = (expenseGroup) =>
  GROUP_CATEGORIES[expenseGroup] || GROUP_CATEGORIES[DEFAULT_EXPENSE_GROUP];

// Every eligible category the app offers, across all four education groups.
// For screens that filter a mixed list and have no single group in hand.
export const ALL_IRS_CATEGORIES = [
  ...new Set(Object.values(GROUP_CATEGORIES).flat()),
].sort();

// The list a screen may offer, or null meaning OFFER NOTHING YET.
// `null` and `[]` are different answers: null is "the question is still open",
// [] is "answered, but this family has not created any categories yet" — which
// the screen should say out loud rather than showing an empty dropdown.
export function categoryChoicesFor({ eligible, expenseGroup, familyCategories }) {
  if (!isEligibilityAnswered(eligible)) return null;
  return eligible
    ? irsCategoriesFor(expenseGroup)
    : (familyCategories || []).filter((c) => c !== OTHER_MANUAL);
}

// What becomes of a category already sitting on the row when ANYTHING that
// decides its list changes.
//
// TWO THINGS DECIDE THE LIST, not one. The 529 answer picks WHICH list, and the
// beneficiary picks WHOSE — their education group selects the IRS list, and the
// family list is kept per beneficiary. A first version of this only reconciled
// on the eligibility answer, so changing a row's beneficiary left a category
// from the previous beneficiary's list sitting on the row, invisible in a
// dropdown that no longer offered it, and it was submitted. Every control that
// can move either input calls this.
//
// A category that is still on the new list is KEPT — that is the common case
// when a receipt reading suggested a real IRS category and the owner then
// confirms the expense is eligible, and throwing it away would make the reading
// worthless. A category that is NOT on the new list is cleared to the
// placeholder, because it belongs to the other list and cannot describe this
// expense. Nothing the account owner typed by hand is lost silently: the only
// values that can be cleared are ones the other list would refuse anyway, and
// the field is left visibly empty ("Select an expense category") rather than
// swapped for a different value.
export function reconcileCategory({ category, eligible, expenseGroup, familyCategories }) {
  const choices = categoryChoicesFor({ eligible, expenseGroup, familyCategories });
  if (!choices) return "";
  return choices.includes(category) ? category : "";
}

// A PICKER MUST BE ABLE TO SHOW THE VALUE THE RECORD ACTUALLY HOLDS.
//
// A dropdown whose value matches none of its options does not go blank and does
// not complain — it silently displays its FIRST option. So a category the record
// really holds, but that the list does not offer, is replaced on screen by
// whatever that family happens to have listed first. The record is right, the
// screen is wrong, and the person reads the screen. That is how ZEN-685 was
// lived: a one-off name was typed and stored, the box showed "Psychotherapy",
// and the account owner reasonably concluded the feature was broken.
//
// A value can end up off its own list two ways, and both are ordinary: the
// owner typed a one-off name (ZEN-685), or a category was later deleted in
// Settings while expenses still carry it (ZEN-372).
//
// The answer is the same in both cases and it is NOT to clear the value — that
// would throw away what the record says. The picker renders the stored value as
// an option of its own, and says underneath that this one is not from the list.
// The receipt-review screen has done exactly this since ZEN-282; these two
// exports are that behaviour moved here so every other picker can share it
// rather than agree with it by hand. Four pickers still do not: the batch
// editor (ZEN-374), the repeating-expense box, the split-expense rows and the
// import review row (ZEN-372 defect 2).
//
// NOT FOR THE 529-ELIGIBLE SIDE. The IRS list is the tax reference and is not
// the family's to extend, so nothing off that list may be offered as a choice
// there — see OTHER_MANUAL above, same ruling.
//
// `offered` IS EVERY VALUE THE PICKER ALREADY RENDERS, not merely the category
// list: a picker also carries entries that OPEN something rather than choose
// something ("Other — enter manually", "＋ Add a category"). Pass those too, or
// a value equal to one of them is called off-list and rendered a SECOND time —
// two options sharing one value, with the action entry labelled as though it
// were a category the owner had typed. (Adversarial review, round 1.)
//
// AND NOTHING MORE THAN THAT. A first attempt at the round-1 finding also
// excluded OTHER_MANUAL from this predicate outright, which was the wrong shape
// and round 2 was right to refuse it: suppressing the OPTION does not stop the
// value being STORED, so the reserved name still reached the record and the box
// then fell back to showing the family's first category — the exact defect
// ZEN-685 exists to end, recreated for that one name. It was worse on the
// receipt review screen, where this predicate serves BOTH sides: an unconditional
// exclusion removed a held-value option the 529-eligible side used to get,
// changing behaviour that was not this ticket's to change.
//
// A value that must never be stored is refused AT THE DOOR by
// isReservedCategoryName below. This predicate stays a plain question about
// what the picker offers.
export const isOffListCategory = (category, offered) =>
  Boolean(category) && !(offered || []).includes(category);

// A PICKER'S ACTION ENTRIES ARE NOT CATEGORIES AND MUST NEVER BE STORED.
//
// The one-off box takes free text, and the entries that open it are ordinary
// readable labels short enough to type — the obvious way to end up typing one is
// copying what is on screen. Stored, such a value is a row whose category names
// an action, which is the thing categoryChoicesFor already refuses to offer.
//
// So the box refuses it, the same way it already refuses an empty name: the Save
// button does not become available. NO NEW SENTENCE APPEARS (repo rule 23) —
// refusing by leaving the button unavailable needs no wording, and inventing an
// explanation here is not this lane's to write.
//
// `actions` is the calling screen's own action values; each picker carries a
// different "add" entry, and OTHER_MANUAL above is the one they share.
export const isReservedCategoryName = (name, actions) =>
  (actions || []).includes((name || "").trim());

// What a picker prints under itself while it is showing such a value, so the
// name is not mistaken for a saved category.
//
// WORDING IS NOT MINE TO WRITE (repo rule 23). This sentence is the receipt
// review screen's own, already on screen in production since ZEN-282, moved
// here unchanged so that the two screens cannot drift apart and so that
// changing it is one edit in one place.
export const offListCategoryHint = (category) => `Custom: ${category}`;
